import {
  calcBaseAnual,
  calcImpactoCaixaFluxo,
  calcImpactoCaixaSegmentos,
  calcRegimeFuturo,
} from "../utils/calculoReforma";
import { acoesPreventivas } from "../mockDataCaixa";

/**
 * Dados da aba Caixa, pelo motor de cálculo. Os valores por mês seguem o motor
 * (total do período ÷ 12), como na metodologia. O texto das ações cita números do
 * projeto, então eles são calculados aqui, no lugar dos valores fixos da lista.
 */

const SEGMENT_TEXTS = {
  veiculos: {
    todayText: "ICMS retido pelo substituto na cadeia. Sem crédito significativo: a empresa não recolhe ICMS na saída.",
    futureText: "CBS+IBS incide sobre a margem, com crédito integral nas compras.",
  },
  pecas: {
    todayText: "ICMS normal com crédito de entradas. PIS/COFINS sobre a margem.",
    futureText: "CBS+IBS incide sobre a margem, com crédito integral nas compras.",
  },
  oficina: {
    todayText: "ISS sobre o faturamento de serviços, sem crédito relevante sobre insumos.",
    futureText: "CBS+IBS incide sobre a receita bruta, com crédito só sobre materiais. Mão de obra não credita.",
  },
};

const number = (v, digits = 1) => (v || 0).toLocaleString("pt-BR", { maximumFractionDigits: digits });

export function buildCash(fiscalData, params) {
  const despesas = fiscalData.creditos?.totalCreditoConfirmado?.valorAnual || 0;
  const base = calcBaseAnual(fiscalData.totalMensal, despesas, params);
  const futuro = calcRegimeFuturo(base, params);
  const { saidaDebito, entradaCredito, pagamentoLiquido, riscoMetricas } = calcImpactoCaixaFluxo(fiscalData.totalMensal, base, futuro, params);
  const { segmentos, oficinaDetalhe: w } = calcImpactoCaixaSegmentos(fiscalData.totalMensal, base, futuro, params);

  const debitMultiplier = saidaDebito.atual ? saidaDebito.futuro / saidaDebito.atual : 0;
  const creditMultiplier = entradaCredito.atual ? entradaCredito.futuro / entradaCredito.atual : 0;
  const rateToday = w.taxaEfetivaAtual * 100;
  const rateFuture = w.taxaEfetivaFutura * 100;
  const maxDelta = Math.max(...segmentos.map((s) => s.deltaMensal));

  return {
    debit: {
      today: saidaDebito.atual,
      future: saidaDebito.futuro,
      percent: saidaDebito.variacaoPercentual * 100,
      multiplier: debitMultiplier,
      lines: saidaDebito.detalhes,
    },
    credit: {
      today: entradaCredito.atual,
      future: entradaCredito.futuro,
      percent: entradaCredito.variacaoPercentual * 100,
      multiplier: creditMultiplier,
      lines: entradaCredito.detalhes,
    },
    net: {
      today: pagamentoLiquido.atual,
      future: pagamentoLiquido.futuro,
      percent: pagamentoLiquido.variacaoPercentual * 100,
    },
    risks: riscoMetricas.map((r) => ({ label: r.label, today: r.atual, future: r.futuro, multiplier: r.multiplicador })),
    segments: segmentos.map((s) => ({
      key: s.id,
      label: s.nome,
      share: s.participacao,
      context: s.contexto,
      today: s.cargaAtualMes,
      future: s.cargaFuturaMes,
      delta: s.deltaMensal,
      mostAffected: s.deltaMensal === maxDelta && maxDelta > 0,
      ...SEGMENT_TEXTS[s.id],
    })),
    workshop: {
      revenue: w.receitaMes,
      labor: w.maoDeObraMes,
      materials: w.materiaisMes,
      issToday: w.issAtualMes,
      grossFuture: w.cbsIbsBrutoMes,
      creditMaterials: w.creditoMateriaisMes,
      loadFuture: w.cargaFuturaOficinaMes,
      rateToday,
      rateFuture,
      nominal: (futuro.cbsRate + futuro.ibsRate) * 100,
      iss: params.issAtualServicos,
      increase: w.aumentoAnual,
    },
    actions: acoesPreventivas.map((a) => ({
      ...a,
      justificativa: a.justificativa
        .replace("~15 p.p.", `~${number(rateFuture - rateToday)} p.p.`)
        .replace("17×", `${number(creditMultiplier, 0)}×`),
    })),
  };
}
